import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Megaphone, BadgeCheck, Globe2, Timer } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/seo";
import { startCheckout } from "@/lib/checkout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const STATUS_STYLE: Record<string, string> = {
  approved: "bg-green-500/15 text-green-400 border-green-500/30",
  pending_review: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
  draft_unpaid: "bg-white/5 text-gray-400 border-white/20",
};

const EMPTY = { company: "", contact_name: "", website: "", title: "", summary: "", body: "", image_url: "" };

export default function PRService() {
  useSEO("PR & Press Release Publication — NewsLayer", "Publish your company's press release on NewsLayer for $99. Editorial review, permanent link, crypto-native audience.");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const { data: mine, refetch } = useQuery({
    queryKey: ["my-pr-submissions"],
    queryFn: async () => (await api.get("/pr-submissions/me")).data,
    enabled: !!user,
  });

  const valid = form.company.length >= 2 && form.contact_name.length >= 2 &&
    form.title.length >= 10 && form.summary.length >= 20 && form.body.length >= 200;

  const submit = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      const res = await api.post("/pr-submissions", {
        ...form, website: form.website || null, image_url: form.image_url || null,
      });
      await startCheckout(res.data.lookup_key, res.data.submission.id);
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const payPending = async (sub: any) => {
    try { await startCheckout("pr_submission", sub.id); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="pr-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">
        <Megaphone className="h-4 w-4" /> PR Publication Service
      </div>
      <h1 className="font-heading font-black text-white text-3xl sm:text-5xl leading-tight max-w-3xl">
        Get your announcement in front of crypto's readers — $99 flat.
      </h1>
      <p className="text-gray-400 mt-5 max-w-2xl text-base sm:text-lg leading-relaxed">
        Submit your press release, pay once, and after editorial review it publishes on NewsLayer
        with a clear <span className="text-white font-semibold">PRESS RELEASE</span> label and a permanent link.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
        {[
          { icon: BadgeCheck, t: "Editorial review", d: "Every release is reviewed before publishing — protecting readers and your reputation." },
          { icon: Globe2, t: "Permanent link", d: "Your announcement lives on NewsLayer with clean formatting and a link to your site." },
          { icon: Timer, t: "Fast turnaround", d: "Most releases are reviewed within one business day of payment." },
        ].map((p) => (
          <div key={p.t} className="bg-[#121212] border border-white/10 rounded-xl p-6">
            <p.icon className="h-7 w-7 text-[#00E5FF] mb-4" />
            <h3 className="font-heading font-bold text-white text-lg">{p.t}</h3>
            <p className="text-sm text-gray-400 mt-2 leading-relaxed">{p.d}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 bg-[#0d1117] border border-[#00E5FF]/20 rounded-xl p-6 sm:p-10">
        <h2 className="font-heading font-bold text-white text-2xl mb-6">Submit your press release</h2>
        {!user ? (
          <div>
            <p className="text-gray-400 mb-4">Sign in or create a free account to submit.</p>
            <Button onClick={() => navigate("/login")} data-testid="pr-signin-button"
              className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">Sign in to continue</Button>
          </div>
        ) : (
          <div className="space-y-4 max-w-2xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Company</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  data-testid="pr-form-company" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
              </div>
              <div>
                <Label>Contact name</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  data-testid="pr-form-contact" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Website (optional)</Label>
                <Input value={form.website} placeholder="https://yourcompany.com"
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  data-testid="pr-form-website" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
              </div>
              <div>
                <Label>Hero image URL (optional)</Label>
                <Input value={form.image_url} placeholder="https://…"
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  data-testid="pr-form-image" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
              </div>
            </div>
            <div>
              <Label>Headline</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Company X launches… (10+ chars)"
                data-testid="pr-form-title" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
            </div>
            <div>
              <Label>Summary</Label>
              <Textarea value={form.summary} rows={2}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                placeholder="One or two sentences (20+ chars)"
                data-testid="pr-form-summary" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
            </div>
            <div>
              <Label>Full release</Label>
              <Textarea value={form.body} rows={10}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Paste the full press release. Blank lines create paragraphs. (200+ chars)"
                data-testid="pr-form-body" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
            </div>
            <Button onClick={submit} disabled={busy || !valid} data-testid="pr-submit-button"
              className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0] px-8">
              {busy ? "Redirecting to checkout…" : "Pay $99 & submit for review"}
            </Button>
          </div>
        )}
      </div>

      {(mine || []).length > 0 && (
        <div className="mt-10">
          <h2 className="font-heading font-bold text-white text-xl mb-4">My submissions</h2>
          <div className="space-y-2">
            {mine.map((s: any) => (
              <div key={s.id} data-testid={`pr-row-${s.id}`}
                className="flex flex-wrap items-center justify-between gap-3 bg-[#121212] border border-white/10 rounded-lg px-4 py-3">
                <div className="min-w-0">
                  {s.status === "approved" && s.article_slug ? (
                    <Link to={`/article/${s.article_slug}`} className="text-white font-medium hover:text-[#00E5FF] transition-colors line-clamp-1">{s.title}</Link>
                  ) : (
                    <p className="text-white font-medium line-clamp-1">{s.title}</p>
                  )}
                  <p className="font-mono text-[10px] text-gray-500 mt-0.5">{s.company} · {timeAgo(s.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={`${STATUS_STYLE[s.status] || "border-white/20 text-gray-300"} font-mono text-[10px] uppercase`}>
                    {s.status === "draft_unpaid" ? "payment pending" : s.status.replace("_", " ")}
                  </Badge>
                  {s.status === "draft_unpaid" && (
                    <Button size="sm" onClick={() => payPending(s)} data-testid={`pr-pay-${s.id}`}
                      className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0] h-7 text-xs">Pay $99</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
